import { FocusScope } from "@react-aria/focus"
import PropTypes from "prop-types"
import { connect } from "react-redux"

import { updateChoice } from "areas/product/store/actions"
import {
  ChoiceOption,
  GradientContainer,
  OptionTitle,
  OptionPrice,
  OptionsSlider,
  SkuIcon,
} from "areas/product/wizard-options/components"
import { IS_JEWLR } from "helpers/application"
import { to_currency } from "helpers/number"
import { useNavigation } from "helpers/use-navigation"
import { cn } from "styles/utils"

const ChoiceOptions = ({
  data,
  selectedValue,
  setChoice,
  styleCode,
  surcharge,
}) => {
  const keyboardProps = useNavigation({ direction: "horizontal" })

  return (
    <OptionsSlider
      aria-label={`${data.heading.title} Options`}
      data-slider="true"
      role="radiogroup"
    >
      {data.options.map((metal) => {
        const selected = selectedValue == metal.value
        const Icon = () => {
          if (metal.value === "CER") {
            return <SkuIcon skuColor="CE">CE</SkuIcon>
          } else if (metal.value === "VERMY") {
            return <SkuIcon skuColor="V">V</SkuIcon>
          } else if (metal.value === "SS14KYP") {
            return <SkuIcon skuColor="YGP">14K</SkuIcon>
          } else if (metal.value === "SS18KYP") {
            return <SkuIcon skuColor="YGP">18K</SkuIcon>
          } else if (metal.value === "SSRGP") {
            return <SkuIcon skuColor="R">R</SkuIcon>
          } else {
            return (
              <SkuIcon skuColor={metal.imageKey.slice(-1)}>
                {metal.icon_text}
              </SkuIcon>
            )
          }
        }
        return (
          <ChoiceOption
            aria-checked={selected}
            className={cn(
              "basis-[22%]",
              styleCode?.startsWith("JWLN0300")
                ? "px-4 pb-8 pt-24"
                : "px-4 pb-8 pt-16"
            )}
            data-cy={`product-wizard-option-sku-${metal.value}`}
            data-is-option="true"
            data-is-selected={selected}
            key={`sliderMetalOption-${metal.value}`}
            onClick={() => setChoice(data.pref, metal)}
            role="radio"
            tabIndex={selected ? 0 : -1}
            {...keyboardProps}
          >
            {IS_JEWLR() &&
              styleCode?.startsWith("JWLN0300") &&
              metal.value.endsWith("KY") && (
                <div className="absolute top-0 w-full text-center">
                  <div
                    className={cn(
                      "text-10 tablet:text-12 bg-black-print rounded-xs m-auto inline-flex w-auto items-center justify-center px-4 font-bold text-white",
                      selected &&
                        "bg-selected rounded-t-xs w-full rounded-b-none"
                    )}
                    style={{ height: "18px" }}
                  >
                    MOST POPULAR
                  </div>
                </div>
              )}
            <Icon />
            <OptionTitle>{metal.title}</OptionTitle>
            <OptionPrice>
              {to_currency(metal.display_price + surcharge)}
            </OptionPrice>
          </ChoiceOption>
        )
      })}
    </OptionsSlider>
  )
}

ChoiceOptions.propTypes = {
  data: PropTypes.object,
  selectedValue: PropTypes.string,
  setChoice: PropTypes.func,
  styleCode: PropTypes.string,
  surcharge: PropTypes.number,
}

const InternalSkuOptions = ({
  data,
  selectedValue,
  setChoice,
  styleCode,
  surcharge,
}) => {
  return (
    <GradientContainer>
      <FocusScope>
        <ChoiceOptions
          data={data}
          selectedValue={selectedValue}
          setChoice={setChoice}
          styleCode={styleCode}
          surcharge={surcharge}
        />
      </FocusScope>
    </GradientContainer>
  )
}

InternalSkuOptions.propTypes = {
  data: PropTypes.object,
  selectedValue: PropTypes.string,
  setChoice: PropTypes.func,
  styleCode: PropTypes.string,
  surcharge: PropTypes.number,
}

const getSelectedValue = (state, ownProps) => {
  if (typeof state.choices[ownProps.pref] === "undefined") {
    return ""
  } else {
    return state.choices[ownProps.pref].value
  }
}

const getData = (state, ownProps) => {
  return state.wizard.data[ownProps.pref]
}

const getSurcharge = (state) => {
  const { metalsIncludeSurchargeFor } = state.wizard
  return metalsIncludeSurchargeFor.reduce((accu, curr) => {
    let surchargeFor = state.choices[curr]
    return accu + (surchargeFor?.display_price || surchargeFor?.price || 0)
  }, 0)
}

const mapStateToProps = (state, ownProps) => {
  return {
    data: getData(state, ownProps),
    selectedValue: getSelectedValue(state, ownProps),
    styleCode: state.wizard.description.style_code,
    surcharge: getSurcharge(state),
  }
}

const mapDispatchToProps = (dispatch, ownProps) => {
  return {
    setChoice: (pref, data) => {
      dispatch(updateChoice(pref, data, true))
      try {
        if (typeof ownProps.callback !== "undefined" && ownProps.callback) {
          ownProps.callback()
        }
      } catch (error) {
        console.log("CALLBACK ERROR:", error)
      }
    },
  }
}

const SkuOptions = connect(
  mapStateToProps,
  mapDispatchToProps
)(InternalSkuOptions)

export default SkuOptions
